/**
 * V38.14 — Simulateur CAP BATI+ CMI.
 *
 * Permet de saisir :
 *  - coût HT construction
 *  - coût TTC construction
 *  - coefficient CAP BATI+ CMI
 *  - option GFA oui/non
 *  - coefficient GFA
 *
 * Calcule en temps réel (debounce 250ms via axios) :
 *  - CAP BATI+ CMI = coût HT × coef
 *  - Option GFA = coût TTC × coef GFA
 *  - FOGEDOM/SCIC 9,50 %
 *  - Expertise préventive 9,50 %
 *  - Total final
 *
 * Route : /simulateurs/cap-bati-plus-cmi
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Calculator, ShieldCheck, FileText, RefreshCw, FileCheck2, Loader2, ExternalLink, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import BackButton from '../components/BackButton';
import CapBatiQuotesPanel from '../components/CapBatiQuotesPanel';
import { CapBatiResultCard } from './CapBatiResultCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ccmi-cap-bati`;

const DEFAULT_FORM = {
  construction_cost_ht: 180000,
  construction_cost_ttc: 195300,
  cap_bati_plus_coefficient: 0.035,
  include_gfa: true,
  gfa_coefficient: 0.012,
  // V38.20 — Forfait revue de conformité CCMI (option juriste)
  include_legal_review: false,
  legal_review_amount_ht: 500,
  legal_review_tva_rate: 0.085,
};
const FOGEDOM_RATE = 0.095;
const EXPERTISE_RATE = 0.095;

const formatPct = (n) => `${(Number(n || 0) * 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} %`;

export default function SimulateurCapBatiPlusCmi() {
  // V38.19 — Pré-remplissage depuis le Cockpit CCMI via query params
  // (?ht=...&ttc=...&type=...&projectId=...&projectRef=...&embed=1)
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const embedMode = queryParams.get('embed') === '1';
  const linkedProjectId = queryParams.get('projectId') || null;
  const linkedProjectRef = queryParams.get('projectRef') || null;

  const initialForm = (() => {
    const ht = parseFloat(queryParams.get('ht'));
    const ttc = parseFloat(queryParams.get('ttc'));
    return {
      ...DEFAULT_FORM,
      ...(isFinite(ht) && ht > 0 ? { construction_cost_ht: ht } : {}),
      ...(isFinite(ttc) && ttc > 0 ? { construction_cost_ttc: ttc } : {}),
    };
  })();

  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // V38.15 — Émission documentaire
  const [issuing, setIssuing] = useState(false);
  const [zippingDocs, setZippingDocs] = useState(false);
  const [issuedDocs, setIssuedDocs] = useState(null);
  const [issueError, setIssueError] = useState(null);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const fetchSimulation = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const body = {
        construction_cost_ht: Number(payload.construction_cost_ht) || 0,
        construction_cost_ttc: Number(payload.construction_cost_ttc) || 0,
        cap_bati_plus_coefficient: Number(payload.cap_bati_plus_coefficient) || 0,
        include_gfa: !!payload.include_gfa,
        gfa_coefficient: Number(payload.gfa_coefficient) || 0,
        fogedom_scic_rate: FOGEDOM_RATE,
        expertise_preventive_rate: EXPERTISE_RATE,
        include_legal_review: !!payload.include_legal_review,
        legal_review_amount_ht: Number(payload.legal_review_amount_ht) || 0,
        legal_review_tva_rate: Number(payload.legal_review_tva_rate) || 0,
      };
      // Validation client : pas d'appel API si valeurs invalides
      if (body.construction_cost_ht <= 0 || body.construction_cost_ttc <= 0 || body.cap_bati_plus_coefficient <= 0) {
        setResult(null);
        return;
      }
      if (body.include_gfa && body.gfa_coefficient <= 0) {
        setResult(null);
        setError('Si GFA activée, coefficient GFA > 0 requis.');
        return;
      }
      const res = await axios.post(`${API}/cap-bati-plus-cmi/simulate`, body);
      setResult(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur calcul');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Recalcul automatique en temps réel (debounce 250 ms)
  useEffect(() => {
    const t = setTimeout(() => fetchSimulation(form), 250);
    return () => clearTimeout(t);
  }, [form, fetchSimulation]);

  // V38.15 — Émission des PDF vérifiables
  const handleIssueDocuments = useCallback(async () => {
    setIssuing(true);
    setIssueError(null);
    setIssuedDocs(null);
    try {
      const body = {
        construction_cost_ht: Number(form.construction_cost_ht) || 0,
        construction_cost_ttc: Number(form.construction_cost_ttc) || 0,
        cap_bati_plus_coefficient: Number(form.cap_bati_plus_coefficient) || 0,
        include_gfa: !!form.include_gfa,
        gfa_coefficient: Number(form.gfa_coefficient) || 0,
        fogedom_scic_rate: FOGEDOM_RATE,
        expertise_preventive_rate: EXPERTISE_RATE,
        include_legal_review: !!form.include_legal_review,
        legal_review_amount_ht: Number(form.legal_review_amount_ht) || 0,
        legal_review_tva_rate: Number(form.legal_review_tva_rate) || 0,
        subject_label: 'Simulation CAP BATI+ CMI publique',
        company: {}, project: {}, beneficiary: {}, metadata: {},
      };
      const res = await axios.post(`${API}/cap-bati-plus-cmi/issue-documents`, body);
      setIssuedDocs(res.data);
      const count = res.data?.issued_count || 0;
      toast.success(`${count} document${count > 1 ? 's' : ''} vérifiable${count > 1 ? 's' : ''} généré${count > 1 ? 's' : ''}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur génération';
      setIssueError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      toast.error('Échec génération des PDF');
    } finally {
      setIssuing(false);
    }
  }, [form]);

  return (
    <div className={embedMode ? "min-h-screen bg-white" : "min-h-screen bg-gradient-to-b from-slate-50 to-white"} data-embed={embedMode ? "1" : "0"}>
      {/* V38.31 — Header Navigation supprimé sur cette page à la demande du client.
          La page est volontairement sans bandeau, pour usage public/embarqué. */}
      <div className={embedMode ? "container mx-auto px-4 pt-6 pb-12" : "container mx-auto px-4 pt-8 pb-12"} data-testid="cap-bati-plus-cmi-page">
        {!embedMode && (
          <BackButton
            homeRoute={{
              super_admin: '/admin/ccmi-dashboard',
              admin: '/admin/ccmi-dashboard',
              cooper: '/admin/ccmi-dashboard',
              expert: '/admin/ccmi-dashboard',
              juriste: '/admin/ccmi-dashboard',
              _default: '/admin/ccmi-dashboard',
            }}
            homeLabel="Cockpit CCMI"
          />
        )}

        {linkedProjectRef && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900" data-testid="cap-bati-linked-project-banner">
            <strong>Projet CCMI lié :</strong> <span className="font-mono">{linkedProjectRef}</span>
            {linkedProjectId && <span className="ml-2 text-xs text-blue-700">({linkedProjectId})</span>}
          </div>
        )}

        <header className={embedMode ? "mb-6" : "mb-8 text-center"}>
          <Badge className="bg-orange-600 text-white mb-3 font-semibold">CCMI · Constructeur Maison Individuelle</Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
            <Calculator className="inline h-7 w-7 mr-2 text-orange-600" />
            Simulateur CAP BATI+ CMI
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl mx-auto">
            Estimez en temps réel votre cotisation CAP BATI+ CMI, l&apos;option GFA
            et les contributions FOGEDOM/SCIC + Expertise préventive (9,50 % chacune).
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ============= SAISIE ============= */}
          <Card className="border-orange-200" data-testid="cap-bati-form-card">
            <CardHeader className="bg-orange-50 rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-600" />
                Paramètres de simulation
              </CardTitle>
              <CardDescription>Coefficients configurables, taux par défaut 9,50 %.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div>
                <Label htmlFor="ht">Coût HT de la construction (€)</Label>
                <Input
                  id="ht"
                  type="number"
                  min="0"
                  step="100"
                  data-testid="input-cost-ht"
                  value={form.construction_cost_ht}
                  onChange={(e) => setField('construction_cost_ht', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ttc">Coût TTC de la construction (€)</Label>
                <Input
                  id="ttc"
                  type="number"
                  min="0"
                  step="100"
                  data-testid="input-cost-ttc"
                  value={form.construction_cost_ttc}
                  onChange={(e) => setField('construction_cost_ttc', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="coef">Coefficient CAP BATI+ CMI</Label>
                <Input
                  id="coef"
                  type="number"
                  min="0"
                  max="1"
                  step="0.001"
                  data-testid="input-cap-bati-coef"
                  value={form.cap_bati_plus_coefficient}
                  onChange={(e) => setField('cap_bati_plus_coefficient', e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Exemple : 0.035 = 3,50 % du coût HT
                </p>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="gfa-toggle" className="text-sm font-medium">
                    <ShieldCheck className="h-4 w-4 inline mr-1 text-emerald-600" />
                    Option GFA (Garantie de Financement d&apos;Achèvement)
                  </Label>
                  <Switch
                    id="gfa-toggle"
                    data-testid="toggle-gfa"
                    checked={form.include_gfa}
                    onCheckedChange={(v) => setField('include_gfa', v)}
                  />
                </div>
                {form.include_gfa && (
                  <div className="mt-3">
                    <Label htmlFor="coef-gfa">Coefficient GFA</Label>
                    <Input
                      id="coef-gfa"
                      type="number"
                      min="0"
                      max="1"
                      step="0.001"
                      data-testid="input-gfa-coef"
                      value={form.gfa_coefficient}
                      onChange={(e) => setField('gfa_coefficient', e.target.value)}
                    />
                    <p className="text-xs text-slate-500 mt-1">Exemple : 0.012 = 1,20 % du coût TTC</p>
                  </div>
                )}
              </div>

              {/* V38.20 — Forfait Revue de conformité CCMI (option juriste) */}
              <div className="border-t pt-4" data-testid="legal-review-section">
                <div className="flex items-center justify-between">
                  <Label htmlFor="legal-toggle" className="text-sm font-medium">
                    <FileCheck2 className="h-4 w-4 inline mr-1 text-red-700" />
                    Forfait Revue de conformité CCMI (juriste)
                  </Label>
                  <Switch
                    id="legal-toggle"
                    data-testid="toggle-legal-review"
                    checked={form.include_legal_review}
                    onCheckedChange={(v) => setField('include_legal_review', v)}
                  />
                </div>
                {form.include_legal_review && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="legal-ht" className="text-xs">Montant HT (€)</Label>
                      <Input
                        id="legal-ht"
                        type="number"
                        min="0"
                        step="10"
                        data-testid="input-legal-review-ht"
                        value={form.legal_review_amount_ht}
                        onChange={(e) => setField('legal_review_amount_ht', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="legal-tva" className="text-xs">TVA (taux)</Label>
                      <Input
                        id="legal-tva"
                        type="number"
                        min="0"
                        max="1"
                        step="0.005"
                        data-testid="input-legal-review-tva"
                        value={form.legal_review_tva_rate}
                        onChange={(e) => setField('legal_review_tva_rate', e.target.value)}
                      />
                    </div>
                    <p className="text-xs text-slate-500 col-span-2">
                      Par défaut 500 € HT, TVA 8,5 % (DOM). Personnalisable par le super admin.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t pt-4 text-xs text-slate-500 space-y-1">
                <div className="flex justify-between"><span>Taux FOGEDOM/SCIC</span><span className="font-mono">{formatPct(FOGEDOM_RATE)}</span></div>
                <div className="flex justify-between"><span>Taux Expertise préventive</span><span className="font-mono">{formatPct(EXPERTISE_RATE)}</span></div>
              </div>

              <Button
                onClick={() => setForm(DEFAULT_FORM)}
                variant="outline"
                size="sm"
                className="w-full"
                data-testid="reset-form-btn"
              >
                <RefreshCw className="h-4 w-4 mr-1" /> Réinitialiser
              </Button>
            </CardContent>
          </Card>

          {/* ============= RESULTAT ============= */}
          <CapBatiResultCard
            result={result}
            form={form}
            issuing={issuing}
            zippingDocs={zippingDocs}
            setZippingDocs={setZippingDocs}
            issuedDocs={issuedDocs}
            issueError={issueError}
            handleIssueDocuments={handleIssueDocuments}
            loading={loading}
            error={error}
          />
        </div>

        <div className="mt-8 text-center text-xs text-slate-500 max-w-2xl mx-auto">
          Cette simulation est indicative. Les coefficients réels (CAP BATI+ et GFA)
          sont validés par votre conseiller CCMI selon la situation du chantier et
          de l&apos;adhérent.
        </div>

        {/* V38.22 — Phase B suite : devis sauvegardés + bulk + export + email */}
        <div className="mt-8" id="cap-bati-quotes-section">
          <CapBatiQuotesPanel
            buildPayload={() => {
              const ht = Number(form.construction_cost_ht) || 0;
              const ttc = Number(form.construction_cost_ttc) || 0;
              const coef = Number(form.cap_bati_plus_coefficient) || 0;
              if (ht <= 0 || ttc <= 0 || coef <= 0) return null;
              return {
                construction_cost_ht: ht,
                construction_cost_ttc: ttc,
                cap_bati_plus_coefficient: coef,
                include_gfa: !!form.include_gfa,
                gfa_coefficient: Number(form.gfa_coefficient) || 0,
                fogedom_scic_rate: FOGEDOM_RATE,
                expertise_preventive_rate: EXPERTISE_RATE,
                include_legal_review: !!form.include_legal_review,
                legal_review_amount_ht: Number(form.legal_review_amount_ht) || 0,
                legal_review_tva_rate: Number(form.legal_review_tva_rate) || 0,
                subject_label: linkedProjectRef
                  ? `Devis CAP BATI+ CMI — Projet ${linkedProjectRef}`
                  : 'Devis CAP BATI+ CMI',
              };
            }}
            projectId={linkedProjectId}
            projectRef={linkedProjectRef}
            onLoadIntoForm={(quote) => {
              const inp = quote?.inputs || {};
              setForm((f) => ({
                ...f,
                construction_cost_ht: Number(inp.construction_cost_ht) || f.construction_cost_ht,
                construction_cost_ttc: Number(inp.construction_cost_ttc) || f.construction_cost_ttc,
                cap_bati_plus_coefficient: Number(inp.cap_bati_plus_coefficient) || f.cap_bati_plus_coefficient,
                include_gfa: typeof inp.include_gfa === 'boolean' ? inp.include_gfa : f.include_gfa,
                gfa_coefficient: Number(inp.gfa_coefficient) || f.gfa_coefficient,
                include_legal_review: !!quote?.include_legal_review,
                legal_review_amount_ht: Number(inp.legal_review_amount_ht) || f.legal_review_amount_ht,
                legal_review_tva_rate: Number(inp.legal_review_tva_rate) || f.legal_review_tva_rate,
              }));
              // Remonte en haut pour que l'utilisateur voie le formulaire prérempli
              window.scrollTo({ top: 0, behavior: 'smooth' });
              toast.success(`Devis ${quote.reference} dupliqué dans le formulaire — ajustez puis "Sauvegarder".`);
            }}
          />
        </div>
      </div>
    </div>
  );
}

